import { useState } from 'react'
import { Box, CircularProgress } from '@mui/material'
import originalLogo from './../../assets/logo/logo.png'
import { useParams } from 'react-router-dom'
import { useStore } from '../../context/StoreProvider'
import { isBootSplashActive } from '../../utils/bootSplash'

const RESERVED_PATHS = new Set([
  'join-network',
  'get-mony',
  'brand-network',
  'privacy-policy',
  'terms-of-use',
])

function resolveStoreSlug(paramSlug) {
  // Prefer live route params / URL — never trust a stale window.__FL_STORE_SLUG first
  if (paramSlug) return paramSlug
  if (typeof window === 'undefined') return null
  const first = (window.location.pathname || '/').split('/').filter(Boolean)[0]
  if (first && !RESERVED_PATHS.has(first) && !first.includes('.')) return first
  if (window.__FL_STORE_SLUG) return window.__FL_STORE_SLUG
  return null
}

function isPlaceholderLogo(url) {
  if (!url || typeof url !== 'string') return true
  return /avatar|placeholder|default[-_]?logo|default[-_]?user|no[-_]?image|ui-avatars|gravatar|blank\.|transparent/i.test(
    url
  )
}

/** Only slug-scoped storage — never global initialStoreLogo / store_logo */
function readCachedStoreLogo(slug) {
  if (!slug || typeof localStorage === 'undefined') return ''
  const candidates = []
  try {
    const branding = JSON.parse(localStorage.getItem(`pwa-branding-${slug}`) || 'null')
    if (branding?.logo) candidates.push(branding.logo)
  } catch (_) { /* ignore */ }
  candidates.push(localStorage.getItem(`store-logo-${slug}`))
  if (localStorage.getItem('initialStoreSlug') === slug) {
    candidates.push(localStorage.getItem('initialStoreLogo'))
  }
  for (const url of candidates) {
    if (url && !isPlaceholderLogo(url)) return url
  }
  return ''
}

function readCachedTheme(slug) {
  if (!slug || typeof localStorage === 'undefined') return '#3C1FAB'
  try {
    const branding = JSON.parse(localStorage.getItem(`pwa-branding-${slug}`) || 'null')
    if (branding?.theme_color) return branding.theme_color
  } catch (_) { /* ignore */ }
  try {
    const settings = JSON.parse(localStorage.getItem(`store_settings-${slug}`) || 'null')
    if (settings?.identity_color) return settings.identity_color
  } catch (_) { /* ignore */ }
  return '#3C1FAB'
}

function resolveStoreLogo(storeLogo, cachedLogo) {
  if (cachedLogo && !isPlaceholderLogo(cachedLogo)) return cachedLogo
  if (storeLogo && !isPlaceholderLogo(storeLogo)) return storeLogo
  return ''
}

/**
 * Loading screen.
 * - Main Faster Link routes: Faster Link logo
 * - Store routes: current-slug branding only, else neutral spinner
 */
const LoadingPage = ({ isShow = true }) => {
  const { slug: paramSlug } = useParams()
  const store = useStore()
  const slug = resolveStoreSlug(paramSlug)
  const isStoreRoute = Boolean(slug)
  const [logoFailed, setLogoFailed] = useState(false)

  const cachedLogo = isStoreRoute ? readCachedStoreLogo(slug) : ''
  // Only use React store context logo when it belongs to the URL slug
  const contextLogo =
    store?.storeData?.username && store.storeData.username === slug
      ? store.storeData.logo
      : ''

  const logoUrl = isStoreRoute
    ? resolveStoreLogo(contextLogo, cachedLogo)
    : originalLogo

  const spinnerColor = isStoreRoute
    ? ((store?.storeData?.username === slug && store?.storeData?.identity_color) ||
        readCachedTheme(slug))
    : '#3C1FAB'

  const bootSplashActive = isStoreRoute && isBootSplashActive()

  if (bootSplashActive) {
    return (
      <Box
        sx={{
          height: '100dvh',
          backgroundColor: 'transparent',
        }}
      />
    )
  }

  const showLogo = isShow && logoUrl && !logoFailed

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100dvh',
        backgroundColor: isStoreRoute ? '#fff' : 'transparent',
      }}
    >
      <CircularProgress
        size={85}
        thickness={2}
        sx={{
          position: 'absolute',
          color: spinnerColor,
        }}
      />
      {showLogo ? (
        <Box
          component="img"
          src={logoUrl}
          alt={isStoreRoute ? 'store' : 'Faster Link'}
          onError={() => setLogoFailed(true)}
          sx={{
            width: 60,
            height: 60,
            objectFit: isStoreRoute ? 'cover' : 'contain',
            zIndex: 1,
            borderRadius: '50%',
          }}
        />
      ) : null}
    </Box>
  )
}

export default LoadingPage
